/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Fragment } from 'preact';
import { RatingStar } from './RatingStar';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { useUser } from '../hooks/UNSAFE_useUser';

type Props = {
  fillRatio: number;
  isDisabled?: boolean;
  isReadonly?: boolean;
  color?: 'neutral' | 'gold';
};

const RatingGaugeItem = ({ fillRatio, isDisabled, isReadonly, color }: Props) => {
  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  if (fillRatio === 1 || fillRatio === 0) {
    return (
      <RatingStar
        isSelected={fillRatio === 1}
        isDisabled={isDisabled}
        isReadOnly={isReadonly}
        color={color}
      />
    );
  }

  // if fillRatio is not 1 or zero, absolutely position selected star above the
  // unselected star and clip both to form partially selected star.

  return (
    <Fragment>
      <div
        class={fractionalStar.base}
        style={{
          clipPath: `inset(0% ${isRtl ? (1 - fillRatio) * 100 : 0}% 0% ${
            isRtl ? 0 : fillRatio * 100
          }%)`
        }}>
        <RatingStar
          isSelected={false}
          isDisabled={isDisabled}
          isReadOnly={isReadonly}
          color={color}
        />
      </div>
      <div
        class={fractionalStar.base}
        style={{
          clipPath: `inset(0% ${isRtl ? 0 : (1 - fillRatio) * 100}% 0% ${
            isRtl ? (1 - fillRatio) * 100 : 0
          }%)`
        }}>
        <RatingStar
          isSelected={true}
          isDisabled={isDisabled}
          isReadOnly={isReadonly}
          color={color}
        />
      </div>
    </Fragment>
  );
};

const fractionalStar = {
  base: cssProps`${{
    top: '0px',
    left: '0px',
    position: 'absolute',
    width: '100%',
    height: '100%'
  }}`
};

export { RatingGaugeItem };
